import { memo, useState } from "react";
import classNames from "classnames";
import { Skeleton } from "./Skeleton";

interface ImageProps {
    src: string;
    alt: string;
    className?: string;
    wrapperClassName?: string;
    /**
     * Optional URL to swap in if `src` fails to load. If the fallback also
     * fails, the broken image is left in place but the skeleton is removed.
     */
    fallbackSrc?: string;
    /**
     * When true, the browser prioritises this image (no lazy-load,
     * `fetchpriority="high"`). Use for above-the-fold images.
     */
    priority?: boolean;
    "data-testid"?: string;
}

const ImageComponent = ({
    src,
    alt,
    className = "object-cover",
    wrapperClassName,
    fallbackSrc,
    priority = false,
    "data-testid": testId = "image",
}: ImageProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [resolvedSrc, setResolvedSrc] = useState(src);
    const [previousSrc, setPreviousSrc] = useState(src);

    if (src !== previousSrc) {
        setPreviousSrc(src);
        setResolvedSrc(src);
        setIsLoaded(false);
    }

    return (
        <div
            className={classNames(
                "relative w-full h-full overflow-hidden bg-base-200",
                wrapperClassName,
            )}
        >
            {!isLoaded && (
                <Skeleton
                    data-testid={`${testId}-skeleton`}
                    className="absolute inset-0 rounded-none"
                />
            )}
            <img
                data-testid={testId}
                src={resolvedSrc}
                alt={alt}
                loading={priority ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={priority ? "high" : "auto"}
                onLoad={() => setIsLoaded(true)}
                onError={() => {
                    if (fallbackSrc && resolvedSrc !== fallbackSrc) {
                        setResolvedSrc(fallbackSrc);
                    } else {
                        setIsLoaded(true);
                    }
                }}
                className={classNames(
                    "w-full h-full transition-opacity duration-300",
                    isLoaded ? "opacity-100" : "opacity-0",
                    className,
                )}
            />
        </div>
    );
};

export const Image = memo(ImageComponent);